import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import config from './config';
import { loadState, removeState } from '../auth/storage';

const client = axios.create({
    baseURL: config.apiUrl,
    headers: { 'Content-Type': 'application/json' },
});

// Attach the bearer token (if any) to every request.
client.interceptors.request.use((cfg) => {
    const token = loadState()?.token;
    if (token) {
        cfg.headers.set('Authorization', `Bearer ${token}`);
    }
    return cfg;
});

// Session-expiry handling. A 401 on an authenticated call means the cookie/session has lapsed.
// The local "signed-in" flag is otherwise sticky (cleared only on explicit logout), so the user
// would sit in a logged-in-looking shell where every call silently fails. Clear it and send them
// to a fresh login that surfaces "your session expired". Excluded: login/2FA endpoints (a 401 there
// is "wrong credentials", handled by the page) and calls made when we don't believe we're signed in.
const AUTH_ENDPOINTS = /^\/?(login|activeDirectory)\//i;

client.interceptors.response.use(
    (res) => res,
    (err: unknown) => {
        const status = err instanceof AxiosError ? err.response?.status : undefined;
        const url = (err instanceof AxiosError ? err.config?.url : undefined) ?? '';
        if (
            status === 401 &&
            !AUTH_ENDPOINTS.test(url) &&
            loadState()?.isSignedin &&
            !window.location.pathname.toLowerCase().startsWith('/login')
        ) {
            removeState();
            window.location.assign('/login?expired=1');
        }
        return Promise.reject(err);
    },
);

export function get<T = unknown>(
    path: string,
    cfg?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
    return client.get<T>(path, cfg);
}

export function post<T = unknown>(
    path: string,
    data?: unknown,
    cfg?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
    return client.post<T>(path, data, cfg);
}

export function put<T = unknown>(
    path: string,
    data?: unknown,
    cfg?: AxiosRequestConfig,
): Promise<AxiosResponse<T>> {
    return client.put<T>(path, data, cfg);
}

/** Narrow an unknown catch value to its HTTP status code, if present. */
export function statusOf(err: unknown): number | undefined {
    if (err instanceof AxiosError) return err.response?.status;
    return undefined;
}

/** Pull a backend-provided failCount off an error/response body, if present. */
export function failCountOf(err: unknown): number | undefined {
    if (err instanceof AxiosError) {
        const data = err.response?.data as { failCount?: number } | undefined;
        return data?.failCount;
    }
    return undefined;
}

export { AxiosError };
